import { Controller } from "@hotwired/stimulus"
import html2canvas from "html2canvas"

export default class extends Controller {
  static targets = [
    "button",
    "panel",
    "mainMenu",
    "feedbackForm",
    "positiveFeedbackForm",
    "negativeFeedbackForm",
    "suggestionForm",
    "successMessage",
    "feedbackText",
    "feedbackAttachment",
    "includeScreenshot"
  ]

  // Use an arrow function so the reference is stable
  handleClickOutside = (event) => {
    if (!this.element.contains(event.target) && this.isPanelVisible()) {
      this.closePanel()
    }
  }

  handleKeydown = (event) => {
    if (!this.isPanelVisible()) {
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      this.closePanel()
      return
    }

    if (event.key !== "Tab") {
      return
    }

    const focusableElements = this.visibleFocusableElements()
    if (focusableElements.length === 0) {
      return
    }

    const firstFocusableElement = focusableElements[0]
    const lastFocusableElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey && document.activeElement === firstFocusableElement) {
      event.preventDefault()
      lastFocusableElement.focus()
    } else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
      event.preventDefault()
      firstFocusableElement.focus()
    }
  }

  connect() {
    document.addEventListener("click", this.handleClickOutside)
    document.addEventListener("keydown", this.handleKeydown)
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside)
    document.removeEventListener("keydown", this.handleKeydown)
  }

  togglePanel() {
    this.isPanelVisible() ? this.closePanel() : this.showPanel()
  }

  showPanel() {
    this.panelTarget.classList.remove("d-none")
    if (this.hasButtonTarget) {
      this.buttonTarget.setAttribute("aria-expanded", "true")
    }
    this.showMainMenu()
  }

  closePanel() {
    const focusWasInsidePanel = this.panelTarget.contains(document.activeElement)
    this.panelTarget.classList.add("d-none")
    this.resetAllViews()
    if (this.hasButtonTarget) {
      this.buttonTarget.setAttribute("aria-expanded", "false")
      if (focusWasInsidePanel) {
        this.buttonTarget.focus()
      }
    }
  }

  // --- DRY helper: hide everything except one target ---
  showOnly(target) {
    this.resetAllViews()
    target.classList.remove("d-none")
    target.classList.add("d-block")

    const focusableElements = this.visibleFocusableElements()
    if (focusableElements.length > 0) {
      focusableElements[0].focus()
    } else {
      this.panelTarget.focus()
    }
  }

  showMainMenu() {
    this.showOnly(this.mainMenuTarget)
  }

  showPositiveFeedback() {
    this.showOnly(this.positiveFeedbackFormTarget)
  }

  showNegativeFeedback() {
    this.showOnly(this.negativeFeedbackFormTarget)
  }

  showFeedback() {
    this.showOnly(this.feedbackFormTarget)
  }
  
  showSuggestion() {
    this.showOnly(this.suggestionFormTarget)
  }

  async submitFeedback(type) {
    const feedbackTextarea = this.feedbackTextTargets.find(target => target.dataset.feedbackType === type)
    const feedback = feedbackTextarea.value.trim()
    if (!feedback) {
      return
    }

    const formData = new FormData()
    formData.append("feedback[type]", type)
    formData.append("feedback[text]", feedback)
    formData.append("feedback[page_url]", window.location.href)
    formData.append("feedback[page_path]", window.location.pathname)
    formData.append("feedback[page_query]", window.location.search)
    formData.append("feedback[page_title]", document.title)
    formData.append("feedback[referrer]", document.referrer)

    const attachmentInput = this.feedbackAttachmentTargets.find(target => target.dataset.feedbackType === type)
    if (attachmentInput?.files?.length) {
      Array.from(attachmentInput.files).forEach((file) => {
        formData.append("feedback[attachments][]", file)
      })
    }

    const includeScreenshotCheckbox = this.includeScreenshotTargets.find(target => target.dataset.feedbackType === type)
    if (includeScreenshotCheckbox?.checked) {
      const screenshotBlob = await this.capturePageScreenshot()
      if (screenshotBlob) {
        formData.append("feedback[screenshot]", screenshotBlob, `page-screenshot-${Date.now()}.png`)
      }
    }

    try {
      const response = await fetch("/help/feedback", {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      })

      if (response.ok) {
        this.resetFeedbackForm(type)
        this.showSuccessMessage()
      } else {
        console.error("Failed to submit feedback")
      }
    } catch (error) {
      console.error("Error submitting feedback:", error)
    }
  }

  async capturePageScreenshot() {
    const screenshotTarget = document.getElementById("main-content") || document.body

    this.element.classList.add("help-widget--capturing")

    try {
      const canvas = await html2canvas(screenshotTarget, {
        ignoreElements: (element) => element.closest('[data-controller~="help-widget"]') !== null
      })

      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), "image/png")
      })
    } finally {
      this.element.classList.remove("help-widget--capturing")
    }
  }

  resetFeedbackForm(type) {
    const feedbackTextarea = this.feedbackTextTargets.find(target => target.dataset.feedbackType === type)
    if (feedbackTextarea) {
      feedbackTextarea.value = ""
    }

    const attachmentInput = this.feedbackAttachmentTargets.find(target => target.dataset.feedbackType === type)
    if (attachmentInput) {
      attachmentInput.value = ""
    }

    const includeScreenshotCheckbox = this.includeScreenshotTargets.find(target => target.dataset.feedbackType === type)
    if (includeScreenshotCheckbox) {
      includeScreenshotCheckbox.checked = false
    }
  }

  submitPositiveFeedback() {
    this.submitFeedback("positive")
  }

  submitNegativeFeedback() {
    this.submitFeedback("negative")
  }

  submitSuggestion() {
    this.submitFeedback("suggestion")
  }

  showSuccessMessage() {
    this.showOnly(this.successMessageTarget)
    setTimeout(() => {
      window.location.reload()
    }, 3000);
  }

  resetAllViews() {
    this.mainMenuTarget.classList.add("d-none")
    this.feedbackFormTarget.classList.add("d-none")
    this.positiveFeedbackFormTarget.classList.add("d-none")
    this.negativeFeedbackFormTarget.classList.add("d-none")
    this.suggestionFormTarget.classList.add("d-none")
    this.successMessageTarget.classList.add("d-none")
  }

  isPanelVisible() {
    return this.hasPanelTarget && !this.panelTarget.classList.contains("d-none")
  }

  visibleFocusableElements() {
    const focusableSelector = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    return Array.from(this.panelTarget.querySelectorAll(focusableSelector)).filter((element) => {
      let ancestor = element
      while (ancestor && ancestor !== this.panelTarget) {
        if (ancestor.classList.contains("d-none")) {
          return false
        }
        ancestor = ancestor.parentElement
      }
      return true
    })
  }
}
